import { TableProps, TableMerge } from './store/types';

// Merges are the only place span info lives — the underlying `cells` grid always stays a full
// rows x cols matrix, so unmerging never loses data that was typed before a merge.
export function findOriginMerge(table: TableProps, row: number, col: number): TableMerge {
  const containing = table.merges.find(
    (m) => row >= m.r && row < m.r + m.rowSpan && col >= m.c && col < m.c + m.colSpan
  );
  return containing || { r: row, c: col, rowSpan: 1, colSpan: 1 };
}

function withoutMerge(merges: TableMerge[], target: TableMerge): TableMerge[] {
  return merges.filter((m) => m !== target);
}

export function mergeRight(table: TableProps, row: number, col: number): TableProps {
  const origin = findOriginMerge(table, row, col);
  const neighborCol = origin.c + origin.colSpan;
  if (neighborCol >= table.cols) return table;

  const neighbor = findOriginMerge(table, origin.r, neighborCol);
  const merges = withoutMerge(withoutMerge(table.merges, origin), neighbor);
  merges.push({
    r: origin.r,
    c: origin.c,
    rowSpan: Math.max(origin.rowSpan, neighbor.rowSpan),
    colSpan: (neighbor.c - origin.c) + neighbor.colSpan,
  });
  return { ...table, merges };
}

export function mergeDown(table: TableProps, row: number, col: number): TableProps {
  const origin = findOriginMerge(table, row, col);
  const neighborRow = origin.r + origin.rowSpan;
  if (neighborRow >= table.rows) return table;

  const neighbor = findOriginMerge(table, neighborRow, origin.c);
  const merges = withoutMerge(withoutMerge(table.merges, origin), neighbor);
  merges.push({
    r: origin.r,
    c: origin.c,
    rowSpan: (neighbor.r - origin.r) + neighbor.rowSpan,
    colSpan: Math.max(origin.colSpan, neighbor.colSpan),
  });
  return { ...table, merges };
}

export function unmergeCell(table: TableProps, row: number, col: number): TableProps {
  const origin = findOriginMerge(table, row, col);
  if (origin.rowSpan <= 1 && origin.colSpan <= 1) return table;
  return { ...table, merges: withoutMerge(table.merges, origin) };
}

export function setCellContent(table: TableProps, row: number, col: number, content: TableProps['cells'][number][number]): TableProps {
  const cells = table.cells.map((r, ri) => (ri === row ? r.map((c, ci) => (ci === col ? content : c)) : r));
  return { ...table, cells };
}

// A cell is "covered" when a merge starting elsewhere spans over it - it has no <td> of its own on the canvas.
export function isCoveredCell(table: TableProps, row: number, col: number): boolean {
  return table.merges.some(
    (m) => row >= m.r && row < m.r + m.rowSpan && col >= m.c && col < m.c + m.colSpan && !(row === m.r && col === m.c)
  );
}

export function tableHeight(table: TableProps): number {
  return table.rowHeights.reduce((sum, h) => sum + (h || 40), 0);
}
